import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Lightbulb, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WeeklyInsight } from '../../types/insight';
interface WeeklyInsightCardProps {
  insight: WeeklyInsight;
  onDismiss?: () => void;
}
export function WeeklyInsightCard({
  insight,
  onDismiss
}: WeeklyInsightCardProps) {
  const getIcon = () => {
    switch (insight.type) {
      case 'improvement':
        return <TrendingDown className="w-6 h-6 text-green-500" data-id="element-1053" />;
      // Down is good for toxicity
      case 'regression':
        return <AlertCircle className="w-6 h-6 text-[#FF0000]" data-id="element-1054" />;
      case 'pattern_emerged':
        return <Lightbulb className="w-6 h-6 text-orange-500" data-id="element-1055" />;
      case 'milestone':
        return <CheckCircle2 className="w-6 h-6 text-blue-500" data-id="element-1056" />;
      default:
        return <Sparkles className="w-6 h-6 text-[#FF0000]" data-id="element-1057" />;
    }
  };
  const getColors = () => {
    switch (insight.type) {
      case 'improvement':
        return 'bg-green-500';
      case 'regression':
        return 'bg-[#FF0000]';
      case 'pattern_emerged':
        return 'bg-orange-500';
      case 'milestone':
        return 'bg-blue-500';
      default:
        return 'bg-[#FF0000]';
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="relative bg-[#0a0a0a] border border-gray-800 p-6" data-id="element-1058">
      {/* Accent Bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${getColors()}`} data-id="element-1059" />

      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-1060" />
      <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-1061" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-1062" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-1063" />

      <div className="flex items-start gap-4" data-id="element-1064">
        <div className="p-2.5 bg-gray-800 border border-gray-700" data-id="element-1065">
          {getIcon()}
        </div>

        <div className="flex-1" data-id="element-1066">
          <div className="flex items-center justify-between mb-2" data-id="element-1067">
            <h3 className="font-bold text-white text-lg tracking-tight uppercase" data-id="element-1068">
              {insight.title}
            </h3>
            {insight.priority === 'high' && <span className="px-2 py-0.5 border border-gray-700 bg-gray-800 text-gray-400 text-[10px] font-mono uppercase tracking-widest" data-id="element-1069">
                Weekly Intel
              </span>}
          </div>

          <p className="text-gray-400 font-mono text-sm leading-relaxed mb-4" data-id="element-1070">
            {insight.description}
          </p>

          {insight.metric && <div className="flex items-center gap-4 mb-4" data-id="element-1071">
              <div className="flex flex-col" data-id="element-1072">
                <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-1073">
                  {insight.metric.label}
                </span>
                <span className="text-2xl font-black text-white font-mono" data-id="element-1074">
                  {insight.metric.value}
                  {insight.metric.unit === '%' && '%'}
                </span>
              </div>

              {insight.metric.change !== 0 && <div className={`flex items-center px-2 py-1 border text-[10px] font-mono uppercase tracking-widest ${insight.metric.direction === 'down' && insight.type === 'improvement' || insight.metric.direction === 'up' && insight.type !== 'regression' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30'}`} data-id="element-1075">
                  {insight.metric.direction === 'up' ? <TrendingUp size={12} className="mr-1" data-id="element-1076" /> : <TrendingDown size={12} className="mr-1" data-id="element-1077" />}
                  {Math.abs(insight.metric.change)}%
                </div>}
            </div>}

          {insight.actionLink && <Link to={insight.actionLink} className="inline-flex items-center text-sm font-mono text-[#FF0000] hover:text-[#FF0000]/80 uppercase tracking-wider transition-colors group" data-id="element-1078">
              {insight.actionText || 'View Details'}
              <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-1079" />
            </Link>}
        </div>
      </div>
    </motion.div>;
}